// Cytactic VPN extension: request page for time-bound VPN access via TEAM.
// Submits a `requests` row with requestType=GROUP_MEMBERSHIP. The teamRouter Lambda
// reads requestType, validates groupId against the server-side allowlist, and
// dispatches to the GrantGroup state machine. Server-side allowlist enforcement
// is the security boundary; this UI dropdown is a UX nicety (it only shows what
// the build-time parameters.js was given via ALLOWED_VPN_GROUP_IDS / VPN_GROUP_DISPLAY_NAMES).
import Form from "@awsui/components-react/form";
import FormField from "@awsui/components-react/form-field";
import Input from "@awsui/components-react/input";
import Multiselect from "@awsui/components-react/multiselect";
import Container from "@awsui/components-react/container";
import Header from "@awsui/components-react/header";
import SpaceBetween from "@awsui/components-react/space-between";
import Button from "@awsui/components-react/button";
import Textarea from "@awsui/components-react/textarea";
import moment from "moment";
import { DatePicker } from "antd";
import "../../index.css";
import React, { useState, useEffect } from "react";
import { requestTeam, getSetting } from "../Shared/RequestService";
import { useHistory } from "react-router-dom";
import params from "../../parameters.json";

function VpnRequest(props) {
  const history = useHistory();
  const vpnGroups = (params && params.vpnGroups) || [];

  // Multi-select: one independent request row per selected group. Each gets its own
  // eligibility check, approver, TTL and revoke -- teamRouter processes the resulting
  // DynamoDB stream records independently, so no backend change is needed.
  const [groups, setGroups] = useState([]);
  const [groupError, setGroupError] = useState("");

  const [duration, setDuration] = useState("");
  const [durationError, setDurationError] = useState("");

  const [justification, setJustification] = useState("");
  const [justificationError, setJustificationError] = useState("");

  // The DatePicker shows moment() via defaultValue, but defaultValue is
  // UNCONTROLLED -- it never reaches React state. Seeding state with the same
  // "now" makes the displayed value real, so submitting without touching the
  // picker works instead of failing validation.
  const [time, setTime] = useState(moment().toDate());
  const [timeError, setTimeError] = useState("");

  const [ticketNo, setTicketNo] = useState("");
  const [ticketError, setTicketError] = useState("");

  const [submitLoading, setSubmitLoading] = useState(false);
  const [maxDuration, setMaxDuration] = useState(9);
  const [ticketRequired, setTicketRequired] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const settings = await getSetting();
        if (settings) {
          if (settings.duration) setMaxDuration(Number(settings.duration));
          if (settings.ticketNo !== undefined) setTicketRequired(Boolean(settings.ticketNo));
        }
      } catch (e) {
        console.warn("Failed to load settings; using defaults.", e);
      }
    })();
  }, []);

  function valid() {
    let ok = true;
    if (!groups.length) {
      setGroupError("Select at least one VPN group");
      ok = false;
    }
    if (!time) {
      setTimeError("Pick a start time");
      ok = false;
    }
    if (!duration || Number(duration) < 1 || Number(duration) > Number(maxDuration)) {
      setDurationError(`Enter a number between 1 and ${maxDuration}`);
      ok = false;
    }
    if (!justification || justification.trim().length < 5) {
      setJustificationError("Provide a business justification (min 5 chars)");
      ok = false;
    }
    if (ticketRequired && !ticketNo) {
      setTicketError("Ticket number required");
      ok = false;
    }
    return ok;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!valid()) return;
    setSubmitLoading(true);

    // We reuse the legacy permission-set fields with sentinels so existing
    // notification/audit/UI code paths that read accountId/role/roleId still render.
    // teamRouter + GrantGroup state machine branch on requestType="GROUP_MEMBERSHIP".
    // username + status are auto-populated by the createRequests VTL resolver:
    //   $ctx.args.input.put("username", $ctx.identity.username)
    //   $ctx.stash.defaultValues.put("status", "pending")
    // email is filled in by the teamRouter Lambda after the first DDB stream event.
    // One row per selected group. We reuse the legacy permission-set fields with
    // sentinels so existing notification/audit/UI code paths that read
    // accountId/role/roleId still render. teamRouter + GrantGroup state machine branch
    // on requestType="GROUP_MEMBERSHIP".
    // username + status are auto-populated by the createRequests VTL resolver:
    //   $ctx.args.input.put("username", $ctx.identity.username)
    //   $ctx.stash.defaultValues.put("status", "pending")
    // email is filled in by the teamRouter Lambda after the first DDB stream event.
    const payloads = groups.map((g) => ({
      requestType: "GROUP_MEMBERSHIP",
      groupId: g.value,
      groupName: g.label,
      accountId: "VPN",
      accountName: g.label,
      role: "VPN Access",
      roleId: g.value,
      duration: duration,
      startTime: time,
      justification: justification,
      ticketNo: ticketNo,
    }));

    try {
      // allSettled, not all: eligibility is evaluated per group, so one group can be
      // refused while the rest go through. Report what actually happened rather than
      // claiming every request landed.
      const results = await Promise.allSettled(
        payloads.map((data) => requestTeam(data))
      );
      const ok = [];
      const failed = [];
      results.forEach((r, i) => {
        // requestTeam swallows its own errors and resolves undefined, so a missing
        // id counts as a failure too.
        if (r.status === "fulfilled" && r.value) {
          ok.push(groups[i].label);
        } else {
          failed.push(groups[i].label);
        }
      });

      const notifications = [];
      if (ok.length) {
        notifications.push({
          type: "success",
          content:
            ok.length === 1
              ? `VPN access request submitted for ${ok[0]}`
              : `${ok.length} VPN access requests submitted: ${ok.join(", ")}`,
          dismissible: true,
          onDismiss: () => props.addNotification([]),
        });
      }
      if (failed.length) {
        notifications.push({
          type: "error",
          content: `Failed to submit: ${failed.join(", ")}. Check the console for details.`,
          dismissible: true,
          onDismiss: () => props.addNotification([]),
        });
      }
      props.addNotification(notifications);

      if (ok.length) {
        history.push("/requests/view");
        props.setActiveHref("/requests/view");
      }
    } catch (err) {
      props.addNotification([
        {
          type: "error",
          content: "Failed to submit VPN request. Check the console for details.",
          dismissible: true,
          onDismiss: () => props.addNotification([]),
        },
      ]);
    } finally {
      setSubmitLoading(false);
    }
  }

  function handleCancel() {
    history.push("/");
    props.setActiveHref("/");
  }

  if (vpnGroups.length === 0) {
    return (
      <Container header={<Header variant="h2">Request VPN access</Header>}>
        <p>
          No VPN groups are configured in this deployment. Set
          <code> ALLOWED_VPN_GROUP_IDS </code> and
          <code> VPN_GROUP_DISPLAY_NAMES </code> in <code>deployment/parameters.sh</code>
          and redeploy to enable this feature.
        </p>
      </Container>
    );
  }

  return (
    <div className="container">
      <Form
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleCancel} variant="link">
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={submitLoading}
              onClick={handleSubmit}
            >
              Submit request
            </Button>
          </SpaceBetween>
        }
        header={
          <Header
            variant="h1"
            description="Request time-bound membership in an IDC VPN group. Membership is added at the start time and automatically removed after the requested duration."
          >
            Request VPN access
          </Header>
        }
      >
        <Container header={<Header variant="h2">Request details</Header>}>
          <SpaceBetween direction="vertical" size="l">
            <FormField
              label="VPN group"
              stretch
              description="Pick the environments whose VPN you need. Each becomes its own request, approved and expiring independently. Only deploy-time-allowlisted groups appear."
              errorText={groupError}
            >
              <Multiselect
                placeholder="Select one or more VPN groups"
                filteringType="auto"
                empty="No VPN groups are allowlisted for this deployment"
                options={vpnGroups.map((g) => ({
                  label: g.displayName,
                  value: g.groupId,
                }))}
                selectedOptions={groups}
                onChange={(event) => {
                  setGroupError();
                  setGroups(event.detail.selectedOptions);
                }}
                selectedAriaLabel="selected"
                deselectAriaLabel={(e) => `Remove ${e.label}`}
              />
            </FormField>
            <FormField
              label="Start time"
              stretch
              description="When VPN access should begin"
              errorText={timeError}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                defaultValue={moment()}
                onChange={(event) => {
                  setTimeError();
                  if (event) setTime(event._d);
                }}
              />
            </FormField>
            <FormField
              label="Duration"
              stretch
              description={`Number of hours of VPN access (1 - ${maxDuration})`}
              errorText={durationError}
            >
              <Input
                value={duration}
                onChange={(event) => {
                  setDurationError();
                  Number(event.detail.value) > Number(maxDuration)
                    ? setDurationError(`Enter a number between 1 and ${maxDuration}`)
                    : setDuration(event.detail.value);
                }}
                type="number"
              />
            </FormField>
            <FormField
              label="Ticket no"
              stretch
              description={ticketRequired ? "Required" : "Optional"}
              errorText={ticketError}
            >
              <Input
                value={ticketNo}
                onChange={(event) => {
                  setTicketError();
                  setTicketNo(event.detail.value);
                }}
              />
            </FormField>
            <FormField
              label="Justification"
              stretch
              description="Business justification for VPN access"
              errorText={justificationError}
            >
              <Textarea
                onChange={({ detail }) => {
                  setJustificationError();
                  setJustification(detail.value);
                }}
                value={justification}
                placeholder="Why do you need VPN access?"
              />
            </FormField>
          </SpaceBetween>
        </Container>
      </Form>
    </div>
  );
}

export default VpnRequest;
